import { FC, useCallback, useState } from 'react'

import { Input, notification } from 'antd'

import { useLoading } from '@data-client/hooks'

import { ApiError } from '@peoplevine/sdk/api'
import Form, { Button, Item } from '@peoplevine/sdk/components/form'
import { VerticalSpace } from '@peoplevine/sdk/components/layout'
import { RenderOn } from '@peoplevine/sdk/components/screen'
import { AccountRegistered } from '@peoplevine/sdk/datasource/account/account'
import { isValidEmail } from '@peoplevine/sdk/helpers/strings'
import useBase64 from '@peoplevine/sdk/hooks/useBase64'
import { useI18n } from '@peoplevine/sdk/providers'
import { useAuth } from '@peoplevine/sdk/providers/Auth'
import { useConfig, withPrefix } from '@peoplevine/sdk/providers/ConfigProvider'
import { useNativeApp } from '@peoplevine/sdk/providers/NativeApp'

const LoginUsernameStep: FC<{
  onSuccess: (account: Omit<AccountRegistered, 'pk'> & { username: string }) => void
  onError: (account: Omit<AccountRegistered, 'pk'> & { username: string }) => void
}> = ({ onSuccess, onError }) => {
  const { validate } = useAuth()
  const { settings } = useConfig().portal
  const { t } = useI18n()
  const handleSubmit = useCallback(
    async (values: { username: string }) => {
      const user = await validate(values.username)
      const type = isValidEmail(values.username) ? 'email' : 'username'
      if (!user) {
        notification.error({
          message: `Invalid ${type}`,
          description: `No account matching the provided ${values.username} was found.`,
        })
      } else {
        if (user.activated) {
          onSuccess({ ...user, username: values.username })
        } else if (user?.isMember || settings?.enableRegistration) {
          onError({ ...user, username: values.username })
        }
      }
    },
    [onError, onSuccess, settings?.enableRegistration, validate],
  )
  const [onFinish, loading] = useLoading(handleSubmit, [handleSubmit])
  return (
    <Form<{ username: string }> name={'login'} layout={'vertical'} onFinish={onFinish}>
      <Item
        preserve
        name={'username'}
        label={'Email or Username'}
        required
        rules={[{ required: true, message: t('Please input your username or email') }]}
      >
        <Input autoFocus type={'text'} autoComplete={'username'} />
      </Item>
      <Button loading={loading} block htmlType={'submit'}>
        Continue
      </Button>
    </Form>
  )
}
type FormValues = { password: string }

interface ILoginPasswordStep {
  // username: string
  username: string
  faceId: boolean
  onSuccess: VoidFunction
  onError: (error: ApiError) => void
}

const LoginPasswordStep: FC<ILoginPasswordStep> = ({ onSuccess, onError, username, faceId }) => {
  const { authenticate } = useAuth()
  const { encode } = useBase64()
  const { dispatch } = useNativeApp()
  const [loading, setLoading] = useState(false)
  const { t } = useI18n()

  const onFormFinish = async (values: FormValues) => {
    setLoading(true)
    dispatch({
      type: 'LOGIN',
      body: {
        username,
        password: encode(values.password),
      },
    })
    await authenticate({
      username: username,
      password: values.password,
      rememberMe: true,
    })
      .then(onSuccess)
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <VerticalSpace>
      <Form<FormValues> onFinish={onFormFinish}>
        <Item
          className={withPrefix('login-form-password')}
          preserve={false}
          name={'password'}
          label={'Password'}
          required
          rules={[{ required: true, message: t('Please input your password') }]}
        >
          <Input.Password disabled={loading} autoComplete={'password'} />
        </Item>
        {faceId && (
          <RenderOn native>
            <Button
              className='login-form-face-id'
              type='link'
              onClick={() =>
                dispatch({
                  type: 'FACE_ID',
                })
              }
              icon={'ios/face-id'}
              htmlType={'button'}
              block
            >
              Use Face ID to log in
            </Button>
          </RenderOn>
        )}
        <Button loading={loading} block htmlType={'submit'}>
          Login
        </Button>
      </Form>
    </VerticalSpace>
  )
}

export { LoginPasswordStep, LoginUsernameStep }
